import { CheckCircle2, DownloadCloud, Info, Send, TriangleAlert } from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { useDownloadReceivables } from "../../api/use-agent-feedback";
import type { ClaimWithRelations } from "../../api/use-claim";
import type { ClaimOverview } from "./agent-feedback-steps";
import { BoxLinks, DividerCenterMedium } from "./box-links";

interface FormUploadDocumentsProps {
  claim: ClaimWithRelations;
  claimOverview: ClaimOverview;
}

/** Port of components/agent-feedback/FormUploadDocuments.vue. */
export function FormUploadDocuments({ claim, claimOverview }: FormUploadDocumentsProps) {
  const downloadReceivables = useDownloadReceivables(claim.id);

  // Kind of legacy - This part might not be really in use anymore
  const isExpectedState =
    claim.claimState.type === "CLAIM_VALIDATION.WAIT_FOR_RECEIVABLE_CONTRACT_SIGNATURES";

  const [signerEmail, setSignerEmail] = useState("");
  const [signerFullName, setSignerFullName] = useState("");

  if (!isExpectedState) {
    return (
      <div>
        <Alert variant="success">
          <CheckCircle2 />
          <AlertTitle>Dossier confirmé</AlertTitle>
          <AlertDescription>
            Vous pouvez désormais générer et télécharger les cessions à faire signer
          </AlertDescription>
        </Alert>

        <div className="mt-12 bg-slate-50">
          <div className="rounded border-2 bg-white px-6 pt-8 pb-8">
            <Button
              size="lg"
              onClick={() => downloadReceivables.mutate()}
              disabled={downloadReceivables.isPending}
            >
              <DownloadCloud className="mr-2 h-5 w-5" />
              Télécharger maintenant les documents
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className="px-4" onSubmit={(event) => event.preventDefault()}>
      <div className="space-y-12">
        <div className="py-6">
          <DividerCenterMedium text="Documents à faire signer" />
          <div className="my-12">
            <Alert variant="info">
              <Info />
              <AlertTitle>Nouveau !</AlertTitle>
              <AlertDescription>
                Vous pouvez désormais télécharger les documents à faire signer, directement en cliquant
                ci-dessous :
              </AlertDescription>
            </Alert>
            <div className="my-6 flex justify-center">
              <Button
                type="submit"
                onClick={() => downloadReceivables.mutate()}
                disabled={downloadReceivables.isPending}
              >
                <DownloadCloud className="mr-2 h-4 w-4" />
                Télécharger les documents
              </Button>
            </div>
            <Can permissions={[...UPCLAIM_ROLES, "CUSTOMER_ACCOUNT_MANAGER"]}>
              <div className="my-6">
                <h3 className="mt-10 text-base leading-7 font-semibold italic">
                  Ou bien, faire signer l'entreprise électroniquement :
                </h3>

                {claimOverview.travelAgencyCompany?.isDirectPassengerContactAllowed ? (
                  <div className="space-y-2">
                    <Input
                      placeholder="Adresse email du référent"
                      value={signerEmail}
                      onChange={(event) => setSignerEmail(event.target.value)}
                    />
                    <Input
                      placeholder="Nom complet du signataire"
                      value={signerFullName}
                      onChange={(event) => setSignerFullName(event.target.value)}
                    />
                    <div className="pt-4">
                      <Button
                        onClick={() => downloadReceivables.mutate()}
                        disabled={downloadReceivables.isPending}
                        className="mx-auto"
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Envoyer la signature électronique
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-12">
                    <Alert variant="warning">
                      <TriangleAlert />
                      <AlertTitle>Signature électronique indisponible</AlertTitle>
                      <AlertDescription>
                        <p>
                          <b>Contact direct interdit :</b> Il faut faire signer l'entreprise ET le passager
                          manuellement
                        </p>
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </div>
            </Can>
          </div>
          <div className="my-24">
            <BoxLinks claimId={claim.id} />
          </div>
        </div>
      </div>
    </form>
  );
}
